<template>
  <div
    class="v-redes-calendar"
    :class="{ [`v-redes-calendar--mode_${mode}`]: mode, 'v-redes-calendar_dense': isDense }"
  >
    <div class="v-redes-calendar__wrapper">
      <button class="v-redes-calendar__arrow" @click="prev">
        <IconComponent class="v-redes-calendar__arrow--icon" name="arrow_back" />
      </button>

      <v-calendar
        ref="calendar"
        :columns="parseInt(columns)"
        :rows="parseInt(rows)"
        :step="parseInt(step)"
        :attributes="attributes"
        :locale="{ id: 'ru', firstDayOfWeek: 2, masks: { weekdays: 'WW' } }"
        fromPage="1"
        @update:pages="onCalendarPages"
        @didMove="(...args) => $emit('didMove', ...args)"
      >
        <template #day-popover="popoverScope">
          <button class="v-redes-calendar__close-popover" @click="popoverScope.hide()">
            <IconComponent name="clear_cross" />
          </button>
          <div class="v-redes-calendar__popover" @wheel.stop>
            <template v-if="$slots.customPopover">
              <div class="day-popover">
                <div class="day-popover__item-body">
                  <slot name="customPopover" v-bind="popoverScope"></slot>
                </div>
              </div>
            </template>
            <template v-else>
              <div
                class="day-popover"
                v-for="{ key, customData } in popoverScope.attributes"
                :key="key"
                :ref="`popover${key}`"
              >
                <div class="day-popover__item-body">
                  <!-- Тип мероприятия -->
                  <p class="type">{{ customData?.eventType?.name }}</p>
                  <!-- Дата мероприятия -->
                  <p class="date">{{ popoverScope.dayTitle }}</p>
                  <!-- Проверка наличия времени -->
                  <p class="time-container">
                    <span class="time-block">
                      <span v-if="customData.time != ''" class="time">{{ customData.time }}</span>
                      <span>
                        <span>Регистрация</span>
                        <span v-if="customData.isRegistrationOpen"> открыта</span>
                        <span v-else> закрыта</span>
                      </span>
                    </span>
                  </p>
                  <!-- Текст мероприятия -->
                  <p class="about">{{ customData.name }}</p>
                  <!-- Просмотр мероприятия -->
                  <ButtonComponent mod="gradient-bg" @click="editCard(customData.id || 0)">Просмотр</ButtonComponent>
                </div>
              </div>
            </template>
          </div>
        </template>
      </v-calendar>

      <button class="v-redes-calendar__arrow v-redes-calendar__arrow--next" @click="next">
        <IconComponent class="v-redes-calendar__arrow--icon" name="arrow_back" />
      </button>
    </div>

    <div v-if="$slots.legend" class="v-redes-calendar__legend" :style="{ '--calendar-legend-columns': columns }">
      <slot name="legend"></slot>
    </div>
  </div>
</template>

<script>
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import IconComponent from '@/common/ui/IconComponent.vue';

  export default {
    name: 'Calendar',
    components: { IconComponent, ButtonComponent },
    props: {
      columns: {
        type: [String, Number],
        default: '3',
      },
      rows: {
        type: [String, Number],
        default: 1,
      },
      step: {
        type: [String, Number],
        default: '1',
      },
      attributes: {
        type: Array,
        default: () => [],
      },
      mode: {
        type: [String, null],
        validator(value) {
          return value === null || ['single'].includes(value);
        },
      },
      isDense: {
        type: Boolean,
        default: false,
      },
      dateNavigation: {
        type: String,
      },
    },
    emits: ['update:pages', 'did-move', 'load-calendar', 'edit-card'],
    data() {
      return {
        // Чтобы не слать load-calendar на каждый тик watchEffect v-calendar при неизменном первом месяце
        lastEmittedCalendarPageKey: null,
      };
    },
    mounted() {
      const currentDate = new Date();
      const monthAdjust = parseInt(this.columns) === 3 ? -1 : 0;
      currentDate.setMonth(currentDate.getMonth() + monthAdjust);
      this.$refs.calendar.move(currentDate);
    },
    methods: {
      onCalendarPages(pages) {
        this.$emit('update:pages', pages);
        if (!pages || !pages.length) return;
        const head = pages[0];
        if (head.month == null || head.year == null) {
          return;
        }
        const key = `${head.year}-${head.month}`;
        if (this.lastEmittedCalendarPageKey === key) {
          return;
        }
        this.lastEmittedCalendarPageKey = key;
        this.$emit('load-calendar', { month: head.month, year: head.year });
      },
      editCard(id) {
        this.$emit('edit-card', id);
      },
      prev() {
        this.$refs.calendar.moveBy(-1);
      },
      next() {
        this.$refs.calendar.moveBy(1);
      },
      navigateToDate(date) {
        if (!date) return;

        this.$nextTick(() => {
          this.$refs.calendar.move(new Date(date));
        });
      },
    },
    watch: {
      dateNavigation: {
        handler(newDate) {
          if (newDate) {
            this.navigateToDate(newDate);
          }
        },
        immediate: true,
        deep: false,
      },
    },
  };
</script>

<style lang="scss" scoped>
  .v-redes-calendar {
    $root: &;
    padding: 30px;
    background: $grey-disabled;
    overflow-x: auto;
    &__wrapper {
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: $laptop) {
        gap: 10px 8px;
        flex-wrap: wrap;

        :deep(.vc-container) {
          flex: 1 1 100%;
          order: -1;
        }
      }
    }
    &__arrow {
      appearance: none;
      border: none;
      padding: 0;
      min-width: 40px;
      height: 40px;
      background: $green-4;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      border-radius: 4px;

      &:hover {
        background: $blue-9;
      }

      &--icon {
        width: 17px;
        height: 14px;
        :deep(svg) {
          width: 100%;
          height: 100%;
        }
        :deep(svg path) {
          fill: $white;
        }
      }

      &--next {
        transform: rotate(180deg);
      }
    }

    &_dense {
      #{$root}__wrapper {
        align-items: flex-start;

        @media (max-width: $laptop) {
          gap: 0;

          :deep(.vc-container) {
            flex: none;
            order: inherit;
          }
        }
      }

      #{$root}__arrow {
        min-width: 24px;
        height: 24px;
        position: relative;
        top: 5px;
        z-index: 1;
        order: inherit !important;

        &:first-child {
          margin-right: -30px;

          @media (max-width: $mobile) {
            margin-right: -60px;
            left: -16px;
          }
        }

        &:last-child {
          margin-left: -30px;

          @media (max-width: $mobile) {
            margin-left: -44px;
          }
        }

        svg {
          width: 10px;
        }
      }

      :deep(.vc-pane-header-wrapper),
      :deep(.vc-pane-layout),
      :deep(.vc-pane) {
        padding: 0;
      }

      :deep(.vc-container) {
        margin-bottom: 0 !important;
      }
    }
  }

  .v-redes-calendar__close-popover {
    all: unset;
    position: absolute;
    top: 8px;
    right: 12px;
    z-index: 99;

    &:hover {
      opacity: 0.8;
    }
  }

  .v-redes-calendar__popover {
    scrollbar-width: 6px;
    scrollbar-color: $light-grey-4 transparent;
    max-height: 220px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
      background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $light-grey-4;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-button {
      display: none;
      opacity: 0;
      background-color: transparent;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: $light-grey-5;
    }
  }

  .v-redes-calendar__legend {
    --pane-width: 350px;
    --container-padding: 10px;
    margin-top: 24px;
    margin-inline: auto;
    max-width: calc(
      var(--pane-width) * var(--calendar-legend-columns) - var(--container-padding) * var(--calendar-legend-columns)
    );

    @media (max-width: $tablet) {
      margin-bottom: 24px;
    }

    @media (max-width: $laptop) {
      --pane-width: 100%;
    }
  }

  .v-redes-calendar--mode_single {
    padding: 0;

    &,
    :deep(.vc-pane-header-wrapper),
    :deep(.vc-pane-layout),
    :deep(.vc-title) {
      background-color: transparent;
    }

    :deep(.vc-header) {
      margin-top: 0;
    }

    :deep(.vc-day),
    :deep(.vc-weekday) {
      font: $fira-16-20;
    }

    @media (max-width: $mobile) {
      :deep(.v-redes-calendar__wrapper) {
        flex-wrap: wrap;
        justify-content: center;
      }

      :deep(.vc-container) {
        margin-bottom: 32px;
        flex: 1 1 100%;
      }

      :deep(.v-redes-calendar__arrow) {
        margin-inline: 4px;
        order: 1;
      }
    }
  }
</style>

<style lang="scss">
  .v-redes-calendar {
    // customize
    .vc-popover-content-wrapper {
      max-width: 531px;
      width: 100%;
      padding: 8px 0 1em;
      z-index: 11;

      &:not([data-popper-placement='bottom']) {
        pointer-events: none;
      }

      @at-root .day-popover {
        & + & {
          margin-top: 24px;
        }
      }

      .day-popover {
        padding: 0 12px;

        &__item-body {
          .type,
          .date,
          .time-container {
            margin-bottom: 8px;
          }
          .time-container,
          .date {
            font-size: 12px;
            line-height: 130%;
            color: $light-blue-3;
          }
          .time-block {
            display: flex;
            span {
              &:not(:last-of-type) {
                margin-right: 4px;
              }
            }
          }
          .type {
            font-weight: 500;
            font-size: 16px;
            line-height: 19px;
            color: $light-blue-3;
            width: 90%;
          }
          .about {
            font-size: 14px;
            line-height: 160%;
            color: $white;
            margin-bottom: 24px;
          }
          button {
            width: 100%;
          }
        }
      }

      .vc-popover-content {
        color: $white;
        background-color: $blue-9;
        border: 1px solid $blue-10;
        border-radius: 0.25rem;
        font-size: 12px;
        font-weight: 500;
        padding: 8px 0;
        margin: 0;
      }

      .vc-nav-popover-container {
        background: $white;
        border: none;
        box-shadow:
          0 4px 10px 0 rgba(128, 144, 160, 0.1),
          0 0 1px 0 rgba(128, 144, 160, 0.81);

        button {
          background: none;
          border: none;
          outline: none;
          font-weight: 400;
          font-size: 13px;
        }

        .vc-popover-caret {
          display: none;
        }

        .vc-nav-header {
          line-height: 40px;

          button {
            border-radius: 0;
            height: 40px;

            &:hover {
              background: $light-grey-7;
            }
          }
        }

        .vc-nav-title {
          font-weight: 700;
          font-size: 18px;
          flex-grow: 1;
        }

        .vc-nav-item {
          width: 100%;

          &.is-active,
          &.is-current {
            box-shadow: none;
            color: $black-st;
          }

          &:hover {
            background: $green-1;
            color: $white;
          }
        }

        .vc-nav-items {
          justify-items: center;
          grid-column-gap: 10px;
          padding: 0;
        }

        .vc-nav-arrow {
          width: 40px;

          svg {
            stroke: $black-st;
          }
        }
      }
    }

    .vc-arrow {
      display: none !important;
    }
    .vc-bordered {
      border: none;
      border-radius: 0;
    }
    .vc-pane-header-wrapper,
    .vc-pane-layout {
      background: $grey-disabled;
      padding: 0 10px;
    }
    .vc-title-wrapper {
      z-index: 1;
    }
    .vc-week {
      background: $white;
    }
    .vc-day.is-not-in-month * {
      opacity: 1;
      color: $light-blue-3;
    }
    .vc-day,
    .vc-weekday {
      margin: 9px 8px;
    }
    .vc-title {
      background: $grey-disabled;
      span {
        font: $fira-24;
        font-weight: 500;
      }
    }
    .vc-header {
      margin-bottom: 10px;
    }
    .vc-pane {
      min-width: 324px;
      padding: 0 15px;
    }
    .vc-dot {
      background-color: $green;
      &:not(:last-child) {
        display: none;
      }
    }
    .vc-day-content:hover {
      background-color: $green;
      border-radius: 0;
      color: $white;
    }
    .vc-day-content:focus {
      background-color: $green;
      border-radius: 0;
      color: $white;
    }
    .vc-nav-items {
      grid-column-gap: 25px;
    }
    .ps {
      max-width: 1120px;
      max-height: 100%;
      padding: 0 12px;
    }
    .vc-popover-content {
      .ps {
        max-height: 220px;
      }
      .ps--active-y > .ps__rail-y {
        width: 10px;
      }
      .ps__thumb-y {
        width: 6px !important;
      }
    }
    // Сбрасываем внутренний отсуп, чтобы не происходило смещение данных при закрытии попапа
    .ps {
      padding: 0 !important;
    }
  }
  @media (max-width: $mobile) {
    .v-redes-calendar {
      padding: 10px;
    }
  }
</style>
